import { BlockSuiteError } from '@blocksuite/global/exceptions';

// more than 100% due to the shadow
const leaveToPercent = `calc(100% + 10px)`;

export interface MenuPopper<T extends HTMLElement> {
  element: T;
  dispose: () => void;
  cancel?: () => void;
}

// store active poppers
const popMap = new WeakMap<HTMLElement, Map<string, MenuPopper<HTMLElement>>>();

function animateEnter(el: HTMLElement) {
  el.style.transform = 'translateY(0)';
}
function animateLeave(el: HTMLElement) {
  el.style.transform = `translateY(${leaveToPercent})`;
}

/** The corner radius of the row a menu opens above; a menu stays on the row's straight top edge. */
function rowCornerRadius(clip: HTMLElement) {
  const row = clip.offsetParent;
  return row ? parseFloat(getComputedStyle(row).borderTopLeftRadius) : 0;
}

/**
 * Keeps a menu centred above its tool. Near the row's end it moves inward just far enough to stay
 * on the row's straight top edge. Returns how to stop following the row's and the menu's size.
 */
function centreAboveTool(
  menu: HTMLElement,
  clip: HTMLElement,
  tool: HTMLElement
) {
  const place = () => {
    const row = clip.getBoundingClientRect();
    const anchor = tool.getBoundingClientRect();
    const inset = rowCornerRadius(clip);
    const width = menu.offsetWidth;
    const centred = anchor.left + anchor.width / 2 - row.left - width / 2;
    const left = Math.max(inset, Math.min(centred, row.width - inset - width));
    menu.style.marginLeft = `${left}px`;
    menu.style.maxWidth = `${Math.max(0, row.width - 2 * inset)}px`;
  };
  const observer = new ResizeObserver(place);
  observer.observe(menu);
  observer.observe(clip);
  place();
  return () => observer.disconnect();
}

export function createPopper<T extends keyof HTMLElementTagNameMap>(
  tagName: T,
  reference: HTMLElement,
  options?: {
    /** transition duration in ms */
    duration?: number;
    onDispose?: () => void;
    setProps?: (ele: HTMLElementTagNameMap[T]) => void;
  }
): MenuPopper<HTMLElementTagNameMap[T]> {
  const duration = options?.duration ?? 230;

  if (!popMap.has(reference)) popMap.set(reference, new Map());
  const elMap = popMap.get(reference);
  // if there is already a popper, cancel leave transition and apply enter transition
  if (elMap && elMap.has(tagName)) {
    const popper = elMap.get(tagName);
    if (popper) {
      popper.cancel?.();
      requestAnimationFrame(() => animateEnter(popper.element));
      return popper as MenuPopper<HTMLElementTagNameMap[T]>;
    }
  }

  const clipWrapper = document.createElement('div');
  const menu = document.createElement(tagName);
  options?.setProps?.(menu);
  clipWrapper.append(menu);
  if (!reference.shadowRoot) {
    throw new BlockSuiteError(
      BlockSuiteError.ErrorCode.ValueNotExists,
      'reference must be a shadow root'
    );
  }
  reference.shadowRoot.append(clipWrapper);

  // apply enter transition
  menu.style.transition = `transform ${duration}ms ease`;
  animateLeave(menu);
  requestAnimationFrame(() => animateEnter(menu));

  Object.assign(clipWrapper.style, {
    height: '100px',
    pointerEvents: 'none',
    position: 'absolute',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    left: '0px',
    bottom: '100%',
    display: 'flex',
    alignItems: 'end',
  });

  Object.assign(menu.style, {
    width: 'max-content',
    bottom: '0%',
    pointerEvents: 'auto',
  });
  const stopCentring = centreAboveTool(menu, clipWrapper, reference);
  const remove = () => {
    stopCentring();
    clipWrapper.remove();
    menu.remove();
    popMap.get(reference)?.delete(tagName);
    options?.onDispose?.();
  };

  const popper: MenuPopper<HTMLElementTagNameMap[T]> = {
    element: menu,
    dispose: () => {
      // apply leave transition
      animateLeave(menu);
      menu.addEventListener('transitionend', remove, { once: true });
      popper.cancel = () => menu.removeEventListener('transitionend', remove);
    },
  };

  popMap.get(reference)?.set(tagName, popper);
  return popper;
}
